import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/** Constant scrolling speed in pixels per second (same on every device / reload). */
const PIXELS_PER_SECOND = 70;

/**
 * MACONDO marquee: constant speed, no hover slow-down, no restart on scroll
 * (mobile browsers fire "resize" when the address bar shows/hides: ignored unless the width changes).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  #lastWidth = 0;

  connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    this.#layout();
    this.#lastWidth = this.offsetWidth;
    this.setAttribute('data-ready', '');

    window.addEventListener('resize', this.#handleResize);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.#layout());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /** (re)build the repeated items + the duplicate and set a duration that keeps the speed constant */
  #layout() {
    const { content, marqueeItems } = this.refs;
    const wanted = this.#calculateNumberOfCopies();
    const current = content.children.length;

    if (wanted > current) this.#addRepeatedItems(wanted - current + 1);
    else if (wanted < current) this.#removeRepeatedItems(current - wanted);

    if (!this.clonedContent) this.#duplicateContent();
    else if (this.clonedContent.children.length !== content.children.length) this.#duplicateContent();

    const gap = parseFloat(getComputedStyle(this.refs.wrapper).columnGap) || 0;
    const distance = content.offsetWidth + gap;
    if (distance > 0) this.style.setProperty('--marquee-speed', `${(distance / PIXELS_PER_SECOND).toFixed(2)}s`);
    void marqueeItems;
  }

  #handleResize = debounce(() => {
    const width = this.offsetWidth;
    if (width === this.#lastWidth) return; // height-only change (mobile address bar): do nothing
    this.#lastWidth = width;
    this.#layout();
  }, 250);

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = /** @type {HTMLElement} */ (marqueeItems[0].cloneNode(true));
      clone.removeAttribute('ref');
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies = 0) {
    const { content } = this.refs;

    for (let i = 0; i < numberOfCopies && content.children.length > 1; i++) {
      content.lastElementChild?.remove();
    }
  }

  #calculateNumberOfCopies() {
    const { marqueeItems } = this.refs;
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth = marqueeItems[0]?.offsetWidth ?? 1;

    return marqueeRepeatedItemWidth === 0 ? 1 : Math.max(1, Math.ceil(marqueeWidth / marqueeRepeatedItemWidth));
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
